"use client";

import { useState, useEffect } from "react";
import styled from "styled-components";
import { useRouter } from "next/navigation";
import MealCard from "@/components/MealCard";
import {
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  where,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import { Meal } from "@/types/meal";
import { getUserLocation, calculateDistance } from "@/utils/distance";

const FeedSection = styled.section`
  padding: 5rem 2rem;
  background: #f9f9f9;

  @media (max-width: 768px) {
    padding: 2rem 1rem;
  }
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  color: #333;
  margin-bottom: 0.5rem;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.125rem;
  color: #666;
`;

const ZipCodePrompt = styled.div`
  background: #fff5f0;
  border: 2px solid #c8553d;
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 2rem;
  text-align: center;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const ZipCodeTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 0.5rem;
`;

const ZipCodeText = styled.p`
  color: #666;
  font-size: 1rem;
  margin-bottom: 1.5rem;
  line-height: 1.6;
`;

const ZipCodeForm = styled.form`
  display: flex;
  gap: 1rem;
  max-width: 400px;
  margin: 0 auto;
  align-items: stretch;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ZipCodeInput = styled.input`
  flex: 1;
  padding: 0.875rem 1rem;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s;

  &:focus {
    outline: none;
    border-color: #c8553d;
  }

  &::placeholder {
    color: #999;
  }
`;

const ZipCodeButton = styled.button`
  padding: 0.875rem 2rem;
  background: #c8553d;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: #b04731;
    transform: translateY(-2px);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
  }
`;

const ErrorText = styled.p`
  color: #c8553d;
  font-size: 0.9rem;
  margin-top: 0.5rem;
`;

const RetryButton = styled.button`
  margin-top: 1rem;
  padding: 0.75rem 1.5rem;
  background: #588157;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background: #3a5a40;
    transform: translateY(-2px);
  }
`;

const MealsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4rem;
`;

const Spinner = styled.div`
  width: 50px;
  height: 50px;
  border: 5px solid #e0e0e0;
  border-top-color: #c8553d;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
`;

const EmptyText = styled.p`
  font-size: 1.125rem;
  color: #666;
`;

export default function HomeMealFeed() {
  const router = useRouter();
  const [meals, setMeals] = useState<Meal[]>([]);
  const [loading, setLoading] = useState(true);
  const [locationError, setLocationError] = useState<string>("");
  const [zipCode, setZipCode] = useState<string>("");
  const [zipCodeLoading, setZipCodeLoading] = useState(false);
  const [zipCodeError, setZipCodeError] = useState<string>("");
  const [userLocation, setUserLocation] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  // Get user location on mount
  useEffect(() => {
    // Check if we have a stored location from zip code
    const storedLocation = sessionStorage.getItem("userLocation");
    const storedZipCode = sessionStorage.getItem("userZipCode");

    if (storedLocation && storedZipCode) {
      try {
        const location = JSON.parse(storedLocation);
        setUserLocation(location);
        setZipCode(storedZipCode);
        setLocationError("");
        return;
      } catch (error) {
        console.error("Error parsing stored location:", error);
        sessionStorage.removeItem("userLocation");
        sessionStorage.removeItem("userZipCode");
      }
    }

    // Try to get browser location
    getUserLocation()
      .then((location) => {
        setUserLocation(location);
        setLocationError("");
        sessionStorage.removeItem("locationDenied");
        // Save location to sessionStorage for persistence
        sessionStorage.setItem("userLocation", JSON.stringify(location));
      })
      .catch((error) => {
        console.error("Location error:", error);
        sessionStorage.setItem("locationDenied", "true");
        setUserLocation(null);
        setLocationError(
          "Share your location to discover amazing artisans near you! Enable location access in your browser or enter your zip code below.",
        );
      });

    // Listen for permission changes (when user grants location access)
    if (navigator.permissions) {
      navigator.permissions
        .query({ name: "geolocation" })
        .then((permissionStatus) => {
          permissionStatus.addEventListener("change", () => {
            if (permissionStatus.state === "granted") {
              // Re-try getting location when permission is granted
              getUserLocation()
                .then((location) => {
                  setUserLocation(location);
                  setLocationError("");
                  sessionStorage.removeItem("locationDenied");
                  sessionStorage.setItem(
                    "userLocation",
                    JSON.stringify(location),
                  );
                })
                .catch((err) => {
                  console.error("Error after permission change:", err);
                });
            }
          });
        })
        .catch((err) => {
          // Permissions API not fully supported
        });
    }
  }, []);

  // Handle retry location button
  const handleRetryLocation = () => {
    getUserLocation()
      .then((location) => {
        setUserLocation(location);
        setLocationError("");
        sessionStorage.removeItem("locationDenied");
        sessionStorage.setItem("userLocation", JSON.stringify(location));
      })
      .catch((error) => {
        console.error("Location error on retry:", error);
        setLocationError(
          "Unable to get your location. Please enable location access in your browser settings or enter your zip code below.",
        );
      });
  };

  // Handle zip code submission
  const handleZipCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!zipCode.trim()) {
      setZipCodeError("Please enter a zip code");
      return;
    }

    const zipRegex = /^\d{5}(-\d{4})?$/;
    if (!zipRegex.test(zipCode.trim())) {
      setZipCodeError("Please enter a valid US zip code");
      return;
    }

    setZipCodeLoading(true);
    setZipCodeError("");

    try {
      const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY;
      if (!apiKey) {
        throw new Error("Google Maps API key not configured");
      }

      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
          zipCode.trim(),
        )}&key=${apiKey}`,
      );

      if (!response.ok) {
        throw new Error("Failed to fetch location data");
      }

      const data = await response.json();

      if (data.status === "OK" && data.results && data.results.length > 0) {
        const location = data.results[0].geometry.location;
        const coords = {
          lat: location.lat,
          lng: location.lng,
        };

        setUserLocation(coords);
        setLocationError("");
        sessionStorage.setItem("userLocation", JSON.stringify(coords));
        sessionStorage.setItem("userZipCode", zipCode.trim());
        sessionStorage.removeItem("locationDenied");
      } else {
        setZipCodeError("Could not find location for this zip code");
      }
    } catch (error) {
      console.error("Geocoding error:", error);
      setZipCodeError("Failed to get location. Please try again.");
    } finally {
      setZipCodeLoading(false);
    }
  };

  // Fetch top-rated meals
  useEffect(() => {
    const fetchTopRatedMeals = async () => {
      try {
        setLoading(true);

        const mealsQuery = query(
          collection(db, "meals"),
          where("available", "==", 0),
          orderBy("rating", "desc"),
          limit(8),
        );

        const snapshot = await getDocs(mealsQuery);
        const mealsList: Meal[] = [];

        snapshot.forEach((doc) => {
          const data = doc.data();

          if (data.disabled === true) {
            return;
          }

          // Calculate distance if user location and meal location are available
          let distance: number | undefined = undefined;

          if (data._geoloc && userLocation) {
            distance = calculateDistance(
              userLocation.lat,
              userLocation.lng,
              data._geoloc.lat,
              data._geoloc.lng,
            );
          }

          // Include all meals regardless of distance
          mealsList.push({
            id: doc.id,
            title: data.title || "Untitled Meal",
            description: data.description || "",
            price: data.costPerItem || data.price || 0,
            image: data.image || "",
            imageAlt: data.imageAlt || "",
            imageAlt2: data.imageAlt2 || "",
            uid: data.uid || "",
            producer: data.producer || "Unknown Artisan",
            rating: data.rating || 0,
            ratingCount: data.ratingCount || 0,
            available: data.available !== undefined ? data.available : 1,
            setFoodCategories: data.setFoodCategories || [],
            mealType: data.mealType || "",
            distance: distance,
          });
        });

        // Sort by distance if location is available, otherwise by rating
        if (userLocation) {
          mealsList.sort((a, b) => {
            if (a.distance === undefined && b.distance === undefined) return 0;
            if (a.distance === undefined) return 1;
            if (b.distance === undefined) return -1;
            return a.distance - b.distance;
          });
        } else {
          // Sort by rating (highest first) when no location
          mealsList.sort((a, b) => b.rating - a.rating);
        }

        setMeals(mealsList.slice(0, 8));
      } catch (error) {
        console.error("Error fetching top-rated meals:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTopRatedMeals();
  }, [userLocation]); // Re-fetch meals when user location changes

  const handleMealClick = (mealId: string) => {
    router.push(`/meals/${mealId}`);
  };

  const handleArtisanClick = (uid: string) => {
    router.push(`/artisan/${uid}`);
  };

  // Show loading state
  if (loading) {
    return (
      <FeedSection>
        <Container>
          <LoadingContainer>
            <Spinner />
          </LoadingContainer>
        </Container>
      </FeedSection>
    );
  }

  if (meals.length === 0) {
    return (
      <FeedSection>
        <Container>
          <SectionHeader>
            <SectionTitle>🌟 Top-Rated Meals</SectionTitle>
            <SectionSubtitle>
              Discover the most loved dishes from our artisan community
            </SectionSubtitle>
          </SectionHeader>
          <EmptyState>
            <EmptyText>
              No meals available at the moment. Check back soon!
            </EmptyText>
          </EmptyState>
        </Container>
      </FeedSection>
    );
  }

  return (
    <>
      <FeedSection>
        <Container>
          <SectionHeader>
            <SectionTitle>🌟 Top-Rated Meals</SectionTitle>
            <SectionSubtitle>
              Discover the most loved dishes from our artisan community
            </SectionSubtitle>
          </SectionHeader>

          <MealsGrid>
            {meals.map((meal) => (
              <MealCard
                key={meal.id}
                meal={meal}
                onClick={() => handleMealClick(meal.id)}
                onArtisanClick={() => handleArtisanClick(meal.uid)}
              />
            ))}
          </MealsGrid>
        </Container>
      </FeedSection>
    </>
  );
}
